/**
 * The script the app injects into every HTML frame document, ahead of the
 * authored HTML so its listeners register first. It speaks the protocol of
 * `html-frame-bridge.ts`.
 */
import {
  FRAME_REPLY,
  FRAME_REQUEST,
  HTML_FRAME_ACTION_MESSAGE,
  HTML_FRAME_CONTEXT_MENU_MESSAGE,
  HTML_FRAME_SIZE_MESSAGE,
} from "./html-frame-bridge";

/** Poster width in image pixels; its height follows the page's ratio. */
const HTML_POSTER_WIDTH = 640;
/**
 * The poster is the top of the page at the page's own width / height,
 * clamped: a very tall page shows its top at 0.75, a short one at 1.9.
 */
const POSTER_MIN_RATIO = 0.75;
const POSTER_MAX_RATIO = 1.9;
const HTML_POSTER_JPEG_QUALITY = 0.8;

/**
 * Runtime injected into every HTML frame document. It answers app requests
 * and reports size changes and right clicks; it only accepts requests from
 * the parent window.
 */
export const HTML_FRAME_BRIDGE_SCRIPT = `<script data-denker-runtime="frame-bridge">
(function(){
  var host=window.parent;
  if(!host||host===window)return;
  function send(message){host.postMessage(message,"*");}
  function num(value){value=Number(value);return isFinite(value)?value:0;}
  var posterLib=null;
  // A trusted-scripts-only document allows scripts by this nonce alone.
  var nonce=(document.currentScript&&document.currentScript.nonce)||"";
  // Evaluates the app-sent capture library once per document, with its own
  // exports object so it neither reads nor sets page globals.
  function loadPosterLib(script){
    if(posterLib||typeof script!=="string"||!script)return posterLib;
    var key="__denkerPoster"+Math.random().toString(36).slice(2);
    var lib={};
    window[key]=lib;
    var el=document.createElement("script");
    el.setAttribute("data-denker-runtime","poster-capture");
    if(nonce)el.nonce=nonce;
    el.textContent="(function(exports,module,define){"+script+"\\n}).call(window,window."+key+",{exports:window."+key+"});";
    try{(document.head||document.documentElement).appendChild(el);}finally{el.remove();delete window[key];}
    posterLib=typeof lib.toJpeg==="function"?lib:null;
    return posterLib;
  }
  function oneLine(value,max){return Array.from(String(value||"").replace(/\\s+/g," ").trim()).slice(0,max).join("");}
  // Any CSS colour as the browser computes it ("rgb(…)"), else "".
  function computedColor(value){
    if(!value||!document.body)return "";
    var probe=document.createElement("i");
    probe.style.color=value;
    if(!probe.style.color)return "";
    document.body.appendChild(probe);
    var color=getComputedStyle(probe).color;
    probe.remove();
    return color;
  }
  function themeColor(){
    var metas=document.querySelectorAll('meta[name="theme-color"]');
    for(var i=0;i<metas.length;i++){
      var media=metas[i].getAttribute("media");
      if(media&&window.matchMedia&&!window.matchMedia(media).matches)continue;
      var color=computedColor(metas[i].getAttribute("content"));
      if(color)return color;
    }
    return "";
  }
  // The first painted background behind the headline: the hero's colour.
  function heroColor(el){
    for(;el&&el.nodeType===1;el=el.parentElement){
      var color=getComputedStyle(el).backgroundColor;
      if(color&&color!=="transparent"&&color!=="rgba(0, 0, 0, 0)")return color;
    }
    return "";
  }
  function firstLine(){
    var meta=document.querySelector('meta[name="description"]');
    var description=oneLine(meta&&meta.getAttribute("content"),200);
    if(description)return description;
    var paragraphs=document.body?document.body.getElementsByTagName("p"):[];
    for(var i=0;i<paragraphs.length&&i<50;i++){
      var text=oneLine(paragraphs[i].textContent,200);
      if(text.length>=20)return text;
    }
    return "";
  }
  function measureCover(){
    var h1=document.querySelector("h1");
    return{
      headline:oneLine(h1&&h1.textContent,160),
      line:firstLine(),
      themeColor:themeColor(),
      heroColor:h1?heroColor(h1):""
    };
  }
  function capturePoster(request){
    var lib=loadPosterLib(request.script),root=document.documentElement;
    if(!lib||!root)return Promise.resolve(null);
    var width=Math.max(1,root.clientWidth);
    var pageHeight=Math.max(1,root.scrollHeight,document.body?document.body.scrollHeight:0);
    var ratio=Math.min(${POSTER_MAX_RATIO},Math.max(${POSTER_MIN_RATIO},width/pageHeight));
    var measure=null;
    try{measure=measureCover();}catch(e){measure=null;}
    return lib.toJpeg(root,{
      width:width,
      height:Math.max(1,Math.round(width/ratio)),
      canvasWidth:${HTML_POSTER_WIDTH},
      canvasHeight:Math.max(1,Math.round(${HTML_POSTER_WIDTH}/ratio)),
      pixelRatio:1,
      quality:${HTML_POSTER_JPEG_QUALITY},
      backgroundColor:typeof request.background==="string"&&request.background?request.background:undefined,
      filter:function(node){return node.nodeName!=="SCRIPT";}
    }).then(function(dataUrl){return{dataUrl:dataUrl,measure:measure};});
  }
  function hostAction(el){
    var args={};
    for(var key in el.dataset)if(key!=="denkerAction")args[key]=el.dataset[key];
    return{kind:"host",name:el.getAttribute("data-denker-action"),args:args};
  }
  function actionAt(x,y){
    var target=document.elementFromPoint(x,y);
    if(!target||typeof target.closest!=="function")return null;
    var hostTarget=target.closest("[data-denker-action]");
    if(hostTarget)return hostAction(hostTarget);
    var anchor=target.closest("a[href]");
    if(anchor){
      var href=anchor.getAttribute("href")||"";
      if(!href||href.charAt(0)==="#")return null;
      try{return{kind:"link",href:new URL(href,document.baseURI).href};}catch(e){return null;}
    }
    if(target.closest("button,input,select,textarea,[contenteditable=true]"))return null;
    var summary=target.closest("summary");
    return summary&&summary.matches("details > summary:first-of-type")?{kind:"disclosure",summary:summary}:null;
  }
  window.addEventListener("message",function(event){
    if(event.source!==host)return;
    var request=event.data;
    if(!request||request.type!=="${FRAME_REQUEST}")return;
    if(request.op==="flush"){
      var edit=typeof window.__denkerTakeHtmlEdits==="function"?window.__denkerTakeHtmlEdits():null;
      var active=document.activeElement;
      if(active&&active!==document.body&&typeof active.blur==="function")active.blur();
      send({type:"${FRAME_REPLY}",requestId:request.requestId,edit:edit});
    }else if(request.op==="scroll-by"){
      window.scrollBy({left:num(request.left),top:num(request.top)});
    }else if(request.op==="action-at"){
      var action=actionAt(num(request.x),num(request.y));
      if(action&&action.kind==="disclosure"&&request.activate){
        action.summary.focus({preventScroll:true});
        action.summary.click();
      }
      send({type:"${FRAME_REPLY}",requestId:request.requestId,action:action&&action.kind==="disclosure"?{kind:"disclosure"}:action});
    }else if(request.op==="capture"){
      var requestId=request.requestId;
      var reply=function(result){send({type:"${FRAME_REPLY}",requestId:requestId,dataUrl:result&&typeof result.dataUrl==="string"?result.dataUrl:null,measure:result?result.measure:null});};
      // A tainted canvas (cross-origin images) or a failed render answers null.
      try{capturePoster(request).then(reply,function(){reply(null);});}catch(e){reply(null);}
    }
  });
  // Capture before authored handlers so the frame menu opens in the app.
  document.addEventListener("contextmenu",function(event){
    event.preventDefault();
    event.stopImmediatePropagation();
    var selection=window.getSelection();
    send({type:"${HTML_FRAME_CONTEXT_MENU_MESSAGE}",x:event.clientX,y:event.clientY,selectedText:selection?selection.toString():""});
  },true);
  // A user's click on an action inside the page. Page script can post the
  // same message, so the app confirms every one before acting.
  document.addEventListener("click",function(event){
    var target=event.target;
    if(!event.isTrusted||!target||typeof target.closest!=="function")return;
    var hostTarget=target.closest("[data-denker-action]");
    if(!hostTarget)return;
    event.preventDefault();
    send({type:"${HTML_FRAME_ACTION_MESSAGE}",action:hostAction(hostTarget)});
  },true);
  var lastWidth=-1,lastHeight=-1,scheduled=false;
  function measure(){
    scheduled=false;
    var root=document.documentElement,body=document.body;
    if(!root)return;
    var width=Math.max(root.scrollWidth,body?body.scrollWidth:0);
    var height=Math.max(root.scrollHeight,body?body.scrollHeight:0);
    if(width===lastWidth&&height===lastHeight)return;
    lastWidth=width;lastHeight=height;
    send({type:"${HTML_FRAME_SIZE_MESSAGE}",width:width,height:height});
  }
  function scheduleMeasure(){
    if(scheduled)return;
    scheduled=true;
    requestAnimationFrame(measure);
  }
  function watch(){
    scheduleMeasure();
    new MutationObserver(scheduleMeasure).observe(document.documentElement,{subtree:true,childList:true,characterData:true,attributes:true});
    if(typeof ResizeObserver==="function")new ResizeObserver(scheduleMeasure).observe(document.documentElement);
  }
  window.addEventListener("load",scheduleMeasure);
  window.addEventListener("resize",scheduleMeasure);
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",watch,{once:true});
  else watch();
})();
</scr${""}ipt>`;
